import axios from "axios";

// The public pages (SME / product / procurement listings) need no login, so
// their API endpoints can ask for a "public session" instead: proof that a real
// browser passed a Cloudflare Turnstile challenge. The challenge is normally
// invisible; its result is traded for a short-lived token that is then sent in
// X-Public-Token. See public-session-interceptor.js for when this runs.

export const PUBLIC_TOKEN_HEADER = "X-Public-Token";

const STORAGE_KEY = "smegp-public-session";
// Stop using a token slightly before the API would reject it.
const EXPIRY_MARGIN_MS = 30 * 1000;
const SCRIPT_TIMEOUT_MS = 10 * 1000;
// Generous: a visitor may have to notice and click an interactive challenge.
const CHALLENGE_TIMEOUT_MS = 2 * 60 * 1000;
const CHALLENGE_PROMPT = "กรุณายืนยันตัวตนเพื่อดูข้อมูล";

let session = null;
let pending = null;

function loadSession() {
  if (session) return session;
  try {
    session = JSON.parse(localStorage.getItem(STORAGE_KEY));
  } catch (error) {
    session = null;
  }
  return session;
}

function saveSession(token, expiresInSec) {
  session = { token, expiresAt: Date.now() + expiresInSec * 1000 };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch (error) {
    // Storage can be unavailable (private mode); the in-memory copy still works.
  }
}

function clearSession() {
  session = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    // Nothing to clear.
  }
}

/** The current token, or null when there is none or it is about to expire. */
export function getPublicToken() {
  const current = loadSession();
  if (!current || !current.token) return null;
  if (Date.now() >= current.expiresAt - EXPIRY_MARGIN_MS) return null;
  return current.token;
}

// public/index.html loads the Turnstile script with `async defer`, so it may
// not have arrived yet — or never will, if a content blocker stops it.
function whenTurnstileReady() {
  return new Promise((resolve, reject) => {
    const startedAt = Date.now();
    (function poll() {
      if (window.turnstile) return resolve(window.turnstile);
      if (Date.now() - startedAt > SCRIPT_TIMEOUT_MS) {
        return reject(new Error("Turnstile script did not load"));
      }
      setTimeout(poll, 100);
    })();
  });
}

// Holds the widget. It takes no space and catches no clicks until Turnstile
// decides the visitor has to interact; only then does it dim the page and say
// why a checkbox appeared.
function createChallengeHost() {
  const root = document.createElement("div");
  root.style.cssText =
    "position:fixed;top:0;right:0;bottom:0;left:0;z-index:2147483000;display:flex;" +
    "flex-direction:column;align-items:center;justify-content:center;pointer-events:none;";

  const prompt = document.createElement("p");
  prompt.textContent = CHALLENGE_PROMPT;
  prompt.style.cssText = "display:none;margin:0 0 12px;color:#fff;font-size:1.1rem;text-align:center;";

  const slot = document.createElement("div");

  root.appendChild(prompt);
  root.appendChild(slot);
  document.body.appendChild(root);

  return {
    slot,
    show() {
      root.setAttribute("role", "dialog");
      root.setAttribute("aria-modal", "true");
      root.setAttribute("aria-label", CHALLENGE_PROMPT);
      root.style.background = "rgba(0,0,0,0.6)";
      root.style.pointerEvents = "auto";
      prompt.style.display = "block";
    },
    hide() {
      root.removeAttribute("role");
      root.removeAttribute("aria-modal");
      root.removeAttribute("aria-label");
      root.style.background = "";
      root.style.pointerEvents = "none";
      prompt.style.display = "none";
    },
    remove() {
      if (root.parentNode) root.parentNode.removeChild(root);
    },
  };
}

function solveChallenge() {
  return whenTurnstileReady().then(
    (turnstile) =>
      new Promise((resolve, reject) => {
        const host = createChallengeHost();
        let widgetId;

        const finish = (settle, value) => {
          clearTimeout(timer);
          try {
            turnstile.remove(widgetId);
          } catch (error) {
            // The widget never rendered.
          }
          host.remove();
          settle(value);
        };
        const timer = setTimeout(
          () => finish(reject, new Error("Turnstile challenge timed out")),
          CHALLENGE_TIMEOUT_MS
        );

        try {
          widgetId = turnstile.render(host.slot, {
            sitekey: process.env.VUE_APP_CLOUD_FLARE_TURNSTILE,
            action: "public_session",
            appearance: "interaction-only",
            callback: (token) => finish(resolve, token),
            "error-callback": (code) => {
              finish(reject, new Error(`Turnstile challenge failed (${code})`));
              return true;
            },
            "before-interactive-callback": () => host.show(),
            "after-interactive-callback": () => host.hide(),
          });
        } catch (error) {
          finish(reject, error);
        }
      })
  );
}

function requestSession(turnstileToken) {
  return axios.post("/smegp/public/session", { token: turnstileToken }).then(({ data }) => {
    saveSession(data.token, data.expiresIn);
    return data.token;
  });
}

/**
 * Runs a challenge and trades it for a fresh token. Concurrent callers share
 * one challenge, so a page firing several requests at once shows at most one.
 */
export function refreshPublicToken() {
  if (!pending) {
    clearSession();
    pending = solveChallenge()
      .then(requestSession)
      .then(
        (token) => {
          pending = null;
          return token;
        },
        (error) => {
          pending = null;
          throw error;
        }
      );
  }
  return pending;
}
